import React, { useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Tabs,
  Tab,
  Paper,
  Alert
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CreateIcon from '@mui/icons-material/Create';
import ListIcon from '@mui/icons-material/List';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PeopleIcon from '@mui/icons-material/People';
import FolderIcon from '@mui/icons-material/Folder';
import LogoutIcon from '@mui/icons-material/ExitToApp';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import InfoIcon from '@mui/icons-material/Info';
import EmailIcon from '@mui/icons-material/Email';
import ThemeIcon from '@mui/icons-material/Brightness4';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SaveIcon from '@mui/icons-material/Save';
import CircularProgress from '@mui/material/CircularProgress';
import {
  cargarPerfilUsuario,
  guardarPerfilUsuario,
  PREFERENCIAS_POR_DEFECTO
} from '../utils/preferenciasUsuario';
import { useSesion, cerrarSesion, ESTADO_SESION } from '../hooks/useSesion';
import { problemaDeContrasena } from '../utils/politicaContrasena';
import { degradadoCabecera, degradadoBarraLateral } from '../utils/estilosDelTema';
import { cambiarMiContrasena, errorDeCambioPropio } from '../services/credencialesUsuario';

const drawerWidth = 280;
const collapsedWidth = 76;

function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // 🔽 Sidebar colapsable (solo escritorio). Se recuerda en el dispositivo.
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('ignia-sidebar-collapsed') === 'true'
  );
  const isCollapsed = !isMobile && collapsed;
  const currentDrawerWidth = isCollapsed ? collapsedWidth : drawerWidth;

  const handleCollapseToggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('ignia-sidebar-collapsed', String(next));
      return next;
    });
  }, []);

  // 🆕 Estados para el modal de configuración
  const [configModal, setConfigModal] = useState(false);
  const [configTab, setConfigTab] = useState(0);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [configForm, setConfigForm] = useState({
    // Cambio de contraseña
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    // Preferencias. Solo queda la que el panel implementa de verdad: los
    // interruptores de "Notificaciones por Email" y "Guardado Automático" se
    // retiraron porque no había nada detrás (no hay correo al administrador y
    // el constructor guarda únicamente al pulsar el botón).
    avisoNuevasRespuestas: PREFERENCIAS_POR_DEFECTO.avisoNuevasRespuestas,
    darkMode: localStorage.getItem('ignia-dark-mode') === 'true',
    // Información personal
    displayName: '',
    email: '',
    phone: ''
  });
  const [configErrors, setConfigErrors] = useState({});
  const [configExito, setConfigExito] = useState('');
  const [configLoading, setConfigLoading] = useState(false);

  // 🔐 La sesión la manda Firebase Auth y el rol sale de `usuarios/{uid}` en
  // Firestore, no de `localStorage` (ver utils/sesion.js y hooks/useSesion.js).
  const { estado: estadoSesion, usuario: user, motivo: motivoSesion, esAdmin: isAdmin } = useSesion();

  // 🔧 Al abrir el modal se lee `usuarios/{uid}` (una lectura), que es donde
  // viven de verdad el nombre, el teléfono y las preferencias. Si Firestore
  // falla, `cargarPerfilUsuario` devuelve la copia local en vez de vaciar el
  // formulario.
  const initializeConfigForm = useCallback(async () => {
    const perfil = await cargarPerfilUsuario();
    setConfigForm(prev => ({
      ...prev,
      displayName: perfil.nombre,
      email: user?.email || '',
      phone: perfil.telefono,
      avisoNuevasRespuestas: perfil.preferencias.avisoNuevasRespuestas,
      darkMode: localStorage.getItem('ignia-dark-mode') === 'true'
    }));
  }, [user]);

  // 🌙 Tema oscuro: se aplica de inmediato (App.js escucha el evento) y se recuerda
  const handleDarkModeToggle = useCallback((checked) => {
    setConfigForm(prev => ({ ...prev, darkMode: checked }));
    localStorage.setItem('ignia-dark-mode', String(checked));
    window.dispatchEvent(new CustomEvent('ignia-theme-change', { detail: checked }));
  }, []);

  // 🔧 OPTIMIZADO: Solo actualizar cuando se abre el modal
  useEffect(() => {
    if (configModal) {
      initializeConfigForm();
    }
  }, [configModal, initializeConfigForm]);

  const menuItems = useMemo(() => {
    const items = [
      { text: 'Inicio', icon: <DashboardIcon />, path: '/admin/dashboard' },
      { text: 'Crear Formulario', icon: <CreateIcon />, path: '/admin/create-form' },
      { text: 'Mis Formularios', icon: <ListIcon />, path: '/admin/forms' },
      { text: 'Formularios Resueltos', icon: <AssignmentTurnedInIcon />, path: '/admin/responses' },
      { text: 'Transferencias', icon: <SwapHorizIcon />, path: '/admin/transfers' },
      { text: 'Grupos', icon: <FolderIcon />, path: '/admin/groups' },
      { text: 'Listas de Datos', icon: <ListAltIcon />, path: '/admin/data-lists' },
    ];
    
    if (isAdmin) {
      items.push({ text: 'Usuarios', icon: <PeopleIcon />, path: '/admin/users' });
    }
    
    return items;
  }, [isAdmin]);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(!mobileOpen);
  }, [mobileOpen]);

  const handleProfileMenuOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleProfileMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // 🆕 Handlers para configuración
  const handleConfigOpen = useCallback(() => {
    setConfigModal(true);
    setConfigTab(0);
    setConfigErrors({});
    setConfigExito('');
  }, []);

  const handleConfigClose = useCallback(() => {
    setConfigModal(false);
    setConfigForm(prev => ({
      ...prev,
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }));
    setConfigErrors({});
  }, []);

  const handleConfigChange = useCallback((field, value) => {
    setConfigForm(prev => ({ ...prev, [field]: value }));
    // Limpiar errores al cambiar
    setConfigErrors(prev => {
      if (prev[field]) {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      }
      return prev;
    });
  }, []);

  const validatePasswordChange = useCallback(() => {
    const errors = {};
    
    if (!configForm.currentPassword) {
      errors.currentPassword = 'Contraseña actual requerida';
    }
    
    // La misma política que el alta y la edición en Usuarios (aquí pedía 6)
    const problema = problemaDeContrasena(configForm.newPassword);
    if (problema) {
      errors.newPassword = problema;
    } else if (configForm.newPassword === configForm.currentPassword) {
      errors.newPassword = 'La nueva contraseña es igual a la actual';
    }
    
    if (configForm.newPassword !== configForm.confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden';
    }
    
    return errors;
  }, [configForm.currentPassword, configForm.newPassword, configForm.confirmPassword]);

  const handlePasswordChange = useCallback(async () => {
    const errors = validatePasswordChange();
    if (Object.keys(errors).length > 0) {
      setConfigErrors(errors);
      return;
    }

    setConfigLoading(true);
    setConfigErrors({});
    setConfigExito('');
    try {
      // Antes esto era un `setTimeout` con un `alert` de éxito: no cambiaba
      // nada. Ahora reautentica con la actual y la cambia en Firebase Auth.
      await cambiarMiContrasena(configForm.currentPassword, configForm.newPassword);

      setConfigForm(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      setConfigExito('Contraseña cambiada correctamente');
    } catch (error) {
      console.error('Error al cambiar la contraseña:', error?.code || error);
      setConfigErrors(errorDeCambioPropio(error));
    } finally {
      setConfigLoading(false);
    }
  }, [validatePasswordChange, configForm.currentPassword, configForm.newPassword]);

  // 💾 Guardado real en `usuarios/{uid}`. El tema oscuro no viaja: es una
  // preferencia del dispositivo y ya se aplica y se recuerda al pulsarlo.
  const handleSavePreferences = useCallback(async () => {
    setConfigLoading(true);
    try {
      await guardarPerfilUsuario({
        nombre: configForm.displayName,
        telefono: configForm.phone,
        preferencias: { avisoNuevasRespuestas: configForm.avisoNuevasRespuestas }
      });
      alert('¡Configuración guardada!');
    } catch (error) {
      console.error('Error al guardar la configuración:', error);
      alert(`No se pudo guardar la configuración: ${error.message}`);
    } finally {
      setConfigLoading(false);
    }
  }, [configForm]);

  // 🔑 Cierra la sesión de Firebase y borra SOLO `token` y `user`.
  // El `localStorage.clear()` que había aquí se llevaba por delante el tema
  // oscuro, el estado del menú, la configuración de columnas de la vista de
  // respuestas y las plantillas guardadas en local (`plantilla_word_*`...),
  // que son la única copia de una plantilla que no llegó a subirse.
  const handleLogout = useCallback(async () => {
    await cerrarSesion();
    navigate('/login');
  }, [navigate]);

  // 🚪 Sin sesión de Firebase (o sin ficha en `usuarios/{uid}`) no se entra.
  useEffect(() => {
    if (estadoSesion === ESTADO_SESION.SIN_SESION) {
      navigate('/login', { replace: true });
    }
  }, [estadoSesion, navigate]);

  // 🎨 Sidebar estilo Kizeo (ver ejemplos_kizeo/estilo_dashboard.png):
  // fondo azul, logo + usuario arriba, items redondeados y acciones al pie
  const drawer = useMemo(() => (
    <Box sx={(theme) => ({
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: degradadoBarraLateral(theme),
      color: 'white'
    })}>
      {/* Logo y título */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: isCollapsed ? 1 : 2.5,
        pt: 3,
        pb: 2
      }}>
        {/* Colapsado no cabe la marca (688x354): se cae al icono cuadrado.
            Fondo blanco porque el icono es transparente y su gris y azul
            marino no se leen sobre el degradado azul del menú. */}
        {isCollapsed ? (
          <Box sx={{
            width: 48,
            height: 48,
            p: 0.5,
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img
              src="/icon-512.png"
              alt="IGNIA"
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          </Box>
        ) : (
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {/* El logo es gris oscuro y azul marino: sobre el degradado azul
                del menú no se leería, así que va sobre un panel blanco. */}
            <Box sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              px: 2,
              py: 1.5,
              display: 'flex',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
            }}>
              <img
                src="/images/agp-marca.png"
                alt="AGP Soluciones con ingeniería"
                style={{
                  width: '100%',
                  maxWidth: '170px',
                  height: 'auto',
                  display: 'block'
                }}
              />
            </Box>
            <Typography
              variant="caption"
              sx={{ opacity: 0.85, display: 'block', mt: 1, textAlign: 'center' }}
              noWrap
            >
              Panel Administrativo
            </Typography>
          </Box>
        )}
      </Box>

      {/* Usuario actual + botón de contraer */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        gap: 2,
        px: isCollapsed ? 1 : 2.5,
        pb: 2.5
      }}>
        <Tooltip title={isCollapsed ? (user?.nombre || user?.displayName || 'Administrador') : ''} placement="right">
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(255,255,255,0.25)', fontSize: '1rem' }}>
            {(user?.nombre || user?.displayName || 'A').charAt(0).toUpperCase()}
          </Avatar>
        </Tooltip>
        {!isCollapsed && (
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {user?.nombre || user?.displayName || 'Administrador'}
            </Typography>
            <Chip
              label={isAdmin ? 'Administrador' : 'Usuario'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.35)'
              }}
            />
          </Box>
        )}
        {/* Contraer: al lado del usuario. Solo en escritorio. */}
        {!isMobile && !isCollapsed && (
          <Tooltip title="Contraer menú">
            <IconButton onClick={handleCollapseToggle} size="small" sx={{ color: 'white' }}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Expandir: contraído el menú mide 76 px, así que no cabe al lado del
          avatar y va debajo. */}
      {!isMobile && isCollapsed && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
          <Tooltip title="Expandir menú" placement="right">
            <IconButton onClick={handleCollapseToggle} size="small" sx={{ color: 'white' }}>
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

      {/* Menú principal */}
      <List sx={{ flexGrow: 1, px: 1.5, py: 2 }}>
        {menuItems.map((item) => {
          const selected = location.pathname === item.path;
          return (
            <Tooltip key={item.text} title={isCollapsed ? item.text : ''} placement="right">
              <ListItem
                button
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                selected={selected}
                sx={{
                  borderRadius: 2,
                  mb: 0.75,
                  color: 'white',
                  border: '1px solid transparent',
                  transition: 'all 0.2s ease',
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  px: isCollapsed ? 1.5 : 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.12)'
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255,255,255,0.18)',
                    border: '1px solid rgba(255,255,255,0.35)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.25)'
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40, color: 'white', justifyContent: 'center' }}>
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: selected ? 700 : 500,
                      fontSize: '0.95rem'
                    }}
                  />
                )}
              </ListItem>
            </Tooltip>
          );
        })}
      </List>

      {/* Acciones al pie: configuración y cerrar sesión */}
      <Box sx={{ px: 1.5, pb: 2 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 1.5 }} />
        <Tooltip title={isCollapsed ? 'Configuración' : ''} placement="right">
          <ListItem
            button
            onClick={handleConfigOpen}
            sx={{
              borderRadius: 2,
              mb: 0.75,
              color: 'white',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              px: isCollapsed ? 1.5 : 2,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.12)'
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40, color: 'white', justifyContent: 'center' }}>
              <SettingsIcon />
            </ListItemIcon>
            {!isCollapsed && (
              <ListItemText primary="Configuración" primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }} />
            )}
          </ListItem>
        </Tooltip>
        <Tooltip title={isCollapsed ? 'Cerrar Sesión' : ''} placement="right">
          <ListItem
            button
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.25)',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              px: isCollapsed ? 1.5 : 2,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.22)'
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40, color: 'white', justifyContent: 'center' }}>
              <LogoutIcon />
            </ListItemIcon>
            {!isCollapsed && (
              <ListItemText primary="Cerrar Sesión" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }} />
            )}
          </ListItem>
        </Tooltip>
      </Box>
    </Box>
  ), [menuItems, location.pathname, isMobile, isCollapsed, handleCollapseToggle, handleLogout, handleConfigOpen, user, isAdmin, navigate]);

  // ⏳ `onAuthStateChanged` tarda un instante en resolver si hay sesión: sin
  // esta pantalla el panel parpadearía o redirigiría en cada recarga.
  if (estadoSesion === ESTADO_SESION.CARGANDO || estadoSesion === ESTADO_SESION.SIN_SESION) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // 🚫 Hay cuenta en Firebase Auth pero Firestore no la respalda (sin ficha,
  // desactivada, o no se pudo leer). No se entra al panel.
  if (estadoSesion !== ESTADO_SESION.ACTIVA) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 3 }}>
        <Paper sx={{ p: 4, maxWidth: 480 }}>
          <Alert severity={estadoSesion === ESTADO_SESION.ERROR ? 'warning' : 'error'} sx={{ mb: 2 }}>
            {motivoSesion || 'No se pudo iniciar la sesión del panel.'}
          </Alert>
          <Button variant="contained" fullWidth onClick={handleLogout}>
            Volver al inicio de sesión
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar solo en móvil: en escritorio el sidebar contiene todo (estilo Kizeo) */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: { md: 'none' },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            IGNIA
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Configuración">
              <IconButton color="inherit" onClick={handleConfigOpen}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Perfil">
              <IconButton
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                  <AccountCircleIcon />
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: currentDrawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: isMobile ? drawerWidth : currentDrawerWidth,
              boxSizing: 'border-box',
              border: 'none',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh'
        }}
      >
        {/* Espaciador del AppBar: solo necesario en móvil */}
        <Toolbar sx={{ display: { md: 'none' } }} />
        {/* Las pantallas se cargan al entrar (React.lazy en App.js): el
            Suspense va AQUÍ para que el menú no desaparezca mientras llegan */}
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
              <CircularProgress />
            </Box>
          }
        >
          <Outlet />
        </Suspense>
      </Box>

      {/* Menu de perfil */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => navigate('/admin/profile')}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mi Perfil</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleConfigOpen}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Configuración</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Cerrar Sesión</ListItemText>
        </MenuItem>
      </Menu>

      {/* 🆕 Modal de Configuración */}
      <Dialog
        open={configModal}
        onClose={handleConfigClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 3, maxHeight: isMobile ? '100vh' : '90vh' }
        }}
      >
        <DialogTitle sx={(theme) => ({
          background: degradadoCabecera(theme),
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        })}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
            <SettingsIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              ⚙️ Configuración del Sistema
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Personaliza tu experiencia en IGNIA
            </Typography>
          </Box>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={configTab}
            onChange={(e, newValue) => setConfigTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab icon={<SecurityIcon />} label="Seguridad" />
            <Tab icon={<NotificationsIcon />} label="Preferencias" />
            <Tab icon={<AccountCircleIcon />} label="Cuenta" />
            <Tab icon={<InfoIcon />} label="Sistema" />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 3, maxHeight: '60vh', overflow: 'auto' }}>
          {/* Tab 0: Seguridad */}
          {configTab === 0 && (
            <Stack spacing={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockIcon color="primary" />
                    Cambiar Contraseña
                  </Typography>
                  
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    <TextField
                      label="Contraseña Actual"
                      type={showPasswords.current ? "text" : "password"}
                      autoComplete="current-password"
                      value={configForm.currentPassword}
                      onChange={(e) => handleConfigChange('currentPassword', e.target.value)}
                      error={!!configErrors.currentPassword}
                      helperText={configErrors.currentPassword}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                            edge="end"
                          >
                            {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        )
                      }}
                    />
                    
                    <TextField
                      label="Nueva Contraseña"
                      type={showPasswords.new ? "text" : "password"}
                      autoComplete="new-password"
                      value={configForm.newPassword}
                      onChange={(e) => handleConfigChange('newPassword', e.target.value)}
                      error={!!configErrors.newPassword}
                      helperText={configErrors.newPassword || "Mínimo 8 caracteres, con mayúscula, minúscula y número"}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                            edge="end"
                          >
                            {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        )
                      }}
                    />
                    
                    <TextField
                      label="Confirmar Nueva Contraseña"
                      type={showPasswords.confirm ? "text" : "password"}
                      autoComplete="new-password"
                      value={configForm.confirmPassword}
                      onChange={(e) => handleConfigChange('confirmPassword', e.target.value)}
                      error={!!configErrors.confirmPassword}
                      helperText={configErrors.confirmPassword}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                            edge="end"
                          >
                            {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        )
                      }}
                    />
                    
                    <Button
                      variant="contained"
                      onClick={handlePasswordChange}
                      disabled={configLoading}
                      startIcon={<LockIcon />}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {configLoading ? 'Cambiando...' : 'Cambiar Contraseña'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
              
              <Alert severity="info">
                <Typography variant="body2">
                  💡 <strong>Consejo de seguridad:</strong> Usa una contraseña fuerte con al menos 8 caracteres, números y símbolos.
                </Typography>
              </Alert>
            </Stack>
          )}

          {/* Tab 1: Preferencias (apariencia + notificaciones) */}
          {configTab === 1 && (
            <Stack spacing={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ThemeIcon color="primary" />
                    Apariencia
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={configForm.darkMode}
                        onChange={(e) => handleDarkModeToggle(e.target.checked)}
                      />
                    }
                    label="🌙 Tema Oscuro"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    El cambio se aplica de inmediato y se recuerda en este dispositivo.
                  </Typography>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🔔 Notificaciones
                  </Typography>

                  <Stack spacing={1} sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={configForm.avisoNuevasRespuestas}
                          onChange={(e) => handleConfigChange('avisoNuevasRespuestas', e.target.checked)}
                        />
                      }
                      label="Avisarme de nuevas respuestas"
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      Muestra un aviso emergente en Inicio cuando llegan respuestas
                      nuevas desde la aplicación móvil. Se guarda en tu perfil, así que
                      te acompaña en cualquier navegador.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          )}

          {/* Tab 2: Cuenta */}
          {configTab === 2 && (
            <Stack spacing={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    👤 Información Personal
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Nombre Completo"
                        value={configForm.displayName}
                        onChange={(e) => handleConfigChange('displayName', e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Email"
                        value={configForm.email}
                        disabled
                        fullWidth
                        helperText="El email no se puede modificar"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Teléfono (Opcional)"
                        value={configForm.phone}
                        onChange={(e) => handleConfigChange('phone', e.target.value)}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Stack>
          )}

          {/* Tab 3: Sistema */}
          {configTab === 3 && (
            <Stack spacing={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🏢 Información del Sistema
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Versión de la Aplicación
                        </Typography>
                        <Typography variant="h6" color="primary">
                          v1.2.0
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Rol de Usuario
                        </Typography>
                        <Chip 
                          label={isAdmin ? "Administrador" : "Usuario"} 
                          color={isAdmin ? "success" : "primary"}
                          variant="filled"
                        />
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Alert severity="success">
                        <Typography variant="body2">
                          ✅ <strong>Sistema funcionando correctamente</strong><br/>
                          Última actualización: {new Date().toLocaleDateString()}
                        </Typography>
                      </Alert>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📞 Soporte Técnico
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    ¿Necesitas ayuda? Contacta con nuestro equipo de soporte.
                  </Typography>
                  
                  <Stack direction="row" spacing={2}>
                    <Button variant="outlined" startIcon={<EmailIcon />}>
                      soporte@agpsoluciones.com
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          )}

          {configErrors.general && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {configErrors.general}
            </Alert>
          )}

          {configExito && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {configExito}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleConfigClose} variant="outlined">
            Cancelar
          </Button>
          
          {configTab !== 0 && (
            <Button 
              variant="contained"
              onClick={handleSavePreferences}
              disabled={configLoading}
              startIcon={<SaveIcon />}
            >
              {configLoading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminLayout; 